import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { FormControl, TextField, InputAdornment, IconButton, Box } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Forked from `_components/CustomStaticDateRangePicker3` for DRV Forecast's
// Lock Date Range and table filter pickers only. The shared component greys
// out/disables every day outside whatever month it thinks is "currently
// visible" (tracked via its own onMonthChange-driven state, which starts out
// of sync with the month the calendar actually opens on). DRV Forecast wants
// no disabled days at all, so this fork simply omits that logic rather than
// changing behavior for the shared component's other consumers.
export default function ForecastDrvDateRangePicker({
  startDate: initialStartDate,
  endDate: initialEndDate,
  label,
  onDateRangeChange,
  disabled,
  minimumDate,
  maximumDate,
  maxDays,
  // External validation, driven by the parent (e.g. "Save & Send was
  // clicked while this field is still empty"). Used as a fallback so an
  // error still shows even if the user never focused/blurred this field.
  error: externalError,
  helperText: externalHelperText,
  // Message shown when the user picks only a start (or only an end) date
  // and then clicks away, leaving the range incomplete.
  requiredErrorMessage = 'Date range is required.',
  // Message shown when a complete range is picked but exceeds `maxDays`.
  maxDaysErrorMessage = 'Date range cannot exceed 31 days.',
  // OPT-IN ONLY. When true, an incomplete selection (only a start or only
  // an end date) gets wiped out on blur AND `onDateRangeChange` is called
  // with { startDate: null, endDate: null } so the parent's value can't
  // go stale/out-of-sync with what's displayed. Defaults to false so
  // existing consumers of this component keep their current data
  // behavior unchanged — only the displayed message text improves for
  // them. Turn this on for forms where a half-picked range must not be
  // treated as (or silently fall back to) a valid value.
  clearIncompleteOnBlur = false,
}) {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  // Holds the *reason* for the current internal validation error.
  // null = no internal error. This is intentionally separate from
  // externalError/externalHelperText, which come from the parent's own
  // validation and are only used as a fallback display.
  const [errorMessage, setErrorMessage] = useState(null);

  // Update state when props change
  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  const formattedDateRange = startDate && endDate
    ? `${dayjs(startDate).format('MM/DD/YYYY')}-${dayjs(endDate).format('MM/DD/YYYY')}`
    : '';

  // Handle date range change from calendar
  const handleOnChange = (dates) => {
    const [start, end] = dates;

    if (start && end) {
      const startDayjs = dayjs(start);
      const endDayjs = dayjs(end);
      const isWithin31Days = endDayjs.diff(startDayjs, 'day') <= 30;

      if (maxDays) {
        if (isWithin31Days) {
          setStartDate(start);
          setEndDate(end);
          setErrorMessage(null);
          setCalendarVisible(false);
          onDateRangeChange?.({ startDate: start, endDate: end });
        } else {
          // Reset the end date so the user can try again, with the
          // maxDays-specific message (NOT the "required" message).
          setStartDate(start);
          setEndDate(null);
          setCalendarVisible(true);
          setErrorMessage(maxDaysErrorMessage);
        }
      } else {
        setStartDate(start);
        setEndDate(end);
        setErrorMessage(null);
        setCalendarVisible(false);
        onDateRangeChange?.({ startDate: start, endDate: end });
      }
    } else {
      // Only a start date has been picked so far — selection still in
      // progress. Don't flag an error yet; that's decided on blur.
      setStartDate(start);
      setEndDate(end);
      setErrorMessage(null);
    }
  };

  const toggleCalendarVisibility = (e) => {
    e.stopPropagation();
    setCalendarVisible((prevState) => !prevState);
  };

  const handleFocus = () => {
    setFocused(true);
    setErrorMessage(null);
  };

  const handleBlur = () => {
    setFocused(false);
    if (!startDate || !endDate) {
      // Incomplete selection (e.g. only a start date was picked, then the
      // user clicked away). Always show the correct message. Only clear
      // the half-finished selection and notify the parent if explicitly
      // opted in — that's a data-flow change other consumers may not
      // expect from this component.
      setErrorMessage(requiredErrorMessage);
      if (clearIncompleteOnBlur) {
        setStartDate(null);
        setEndDate(null);
        onDateRangeChange?.({ startDate: null, endDate: null });
      }
    } else {
      setErrorMessage(null);
    }
  };

  const showError = !!errorMessage || !!externalError;
  const helperTextToShow = errorMessage || (externalError ? externalHelperText : '') || '';

  return (
    <FormControl fullWidth margin="normal" className='DateRange' style={{ position: 'relative' }}>
      <TextField
        value={formattedDateRange}
        onClick={toggleCalendarVisibility}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={showError}
        className="selecticon "
        helperText={helperTextToShow}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleCalendarVisibility}>
                <CalendarTodayIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={disabled || false}
        placeholder="Select a date range"
        variant="outlined"
        label={(!startDate && !endDate) ? (label || 'Select Date Range') : ""} // Conditionally remove label if no date is selected
      />

      {/* Date Range Picker - only visible when `calendarVisible` is true */}
      {calendarVisible && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1,
            mt: 1,
            boxShadow: 3,
            borderRadius: 1,
            backgroundColor: 'white',
          }}
        >
          <DatePicker
            selected={startDate}
            onChange={handleOnChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            minDate={minimumDate}
            maxDate={maximumDate}
          />

        </Box>
      )}
    </FormControl>
  );
}
